import ActionsPopover from "@/components/ActionsPopOver/ActionsPopOver";
import ActionsPopOverItem from "@/components/ActionsPopOver/ActionsPopOverItem";
import MoreButton from "@/components/MoreButton/MoreButton";
import PageButtonGroup from "@/components/PageButtonGroup/PageButtonGroup";
import { PageHeader } from "@/components/PageHeader/PageHeader";
import StatusTag from "@/components/StatusTag/StatusTag";
import StatusTags from "@/components/StatusTags/StatusTags";
import SwitchWithLabel from "@/components/SwitchWithLabel/SwitchWithLabel";
import Table, { Column, TableProps } from "@/components/Table/Table";
import useTableStateUrl from "@/components/Table/hooks/useTableStateUrl";
import TableHeader from "@/components/TableHeader/TableHeader";
import YearSelector from "@/components/YearSelector/YearSelector";
import CrossReference from "@/components/links/CrossReference/CrossReference";
import { useCompanySettings, usePatchCompanySettings } from "@/hooks/useCompanySettings";
import useTrackOnLoad from "@/hooks/useTrackPageview";
import useTranslation from "@/hooks/useTranslation";
import {
  RouteGroup,
  useGetOutboundTransport,
  useGetOutboundTransportTotals,
  useOutboundTransportYearStatus,
} from "@/hooks/useTransport";
import { useDashboardYear } from "@/pages/dashboard/hooks/useDateRange";
import { BranchesOutlined, DeleteOutlined, ExportOutlined, MoreOutlined } from "@ant-design/icons";
import { Button, Tooltip } from "antd";
import { useState } from "react";
import { default as DeleteOutboundTransportAction } from "../DeleteOutboundTransportAction";
import ExportOutboundTransportAction from "../ExportOutboundTransportAction";
import OutboundRouteWizard from "../RouteWizard/OutboundRouteWizard";
import "../Transport.scss";
import { transportUploadFilters } from "../constants";
import OutboundTransportButtonGroup from "./OutboundTransportButtonGroup";

const rowLink = (record: RouteGroup, byCategory: boolean) => {
  const arrivalParam = byCategory
    ? record.transport_category || ""
    : record.arrival_id || record.arrival_name;
  const byArrivalFacility = !byCategory && !!record.arrival_id;
  return `/transport/outbound/${record.departure_id}/${encodeURIComponent(
    arrivalParam
  )}?by_category=${byCategory}&arrival_facility=${byArrivalFacility}`;
};

export default function OutboundTransport() {
  const translation = useTranslation();

  useTrackOnLoad("Outbound Transport Viewed");

  const { data: companySettings } = useCompanySettings();
  const byCategory = companySettings?.transport_show_by_category || false;
  const { year } = useDashboardYear();
  const [isMoreOpen, setIsMoreOpen] = useState(false);

  let columns: Column<RouteGroup>[] = [
    {
      key: "departure_name",
      label: translation.transport.departureFacility,
      searchable: true,
      minWidth: "210px",
      noLink: true,
      renderer: (record) => (
        <CrossReference
          to={"/facilities/" + record.departure_id}
          secondaryTo={rowLink(record, byCategory)}
        >
          {record.departure_name}
        </CrossReference>
      ),
    },
    byCategory
      ? {
          key: "transport_category",
          label: translation.transport.transportCategory,
          searchable: true,
          minWidth: "230px",
        }
      : {
          key: "arrival_name",
          label: translation.transport.arrivalFacility,
          searchable: true,
          minWidth: "190px",
          noLink: true,
          renderer: (record) =>
            record.arrival_id ? (
              <CrossReference
                to={"/facilities/" + record.arrival_id}
                secondaryTo={rowLink(record, byCategory)}
              >
                {record.arrival_name}
              </CrossReference>
            ) : (
              record.arrival_name
            ),
        },
    {
      key: "transport_mode",
      minWidth: "200px",
      label: translation.transport.modeOfTransport,
    },
    {
      key: "orders_on_route",
      minWidth: "200px",
      label: translation.transport.ordersOnRoute,
      sortable: true,
    },
    {
      label: translation.transport.status,
      key: "status",
      filterable: true,
      minWidth: "130px",
      renderer: (route) => (
        <Tooltip
          title={
            route.status === "calculating"
              ? "Root is calculating the impact of this route. All you have to do is wait. ⏳"
              : route.status === "incomplete"
                ? "Some of the legs in this route are either incomplete or have errors. Click to investigate. 🧐"
                : undefined
          }
        >
          <>
            <StatusTag status={route.status} />
          </>
        </Tooltip>
      ),
    },
    {
      key: "actions",
      label: translation.transport.actions,
      minWidth: "80px",
      maxWidth: "80px",
      noLink: true,
      renderer: (route) => (
        <ActionsPopover
          zIndex={3}
          actions={
            <>
              <OutboundRouteWizard
                departureId={route.departure_id}
                arrivalName={byCategory ? route.transport_category : route.arrival_name}
                arrivalId={route.arrival_id}
                byTransportCategory={byCategory}
                year={year}
                placement={"left"}
              >
                <ActionsPopOverItem icon={<BranchesOutlined />}>Edit</ActionsPopOverItem>
              </OutboundRouteWizard>
              <ExportOutboundTransportAction
                route={{
                  departure_facility_id: route.departure_id,
                  arrival_facility_id: route.arrival_id,
                  arrival_name: byCategory ? route.transport_category : route.arrival_name,
                  by_transport_category: byCategory,
                }}
                year={year}
                placement={"left"}
              >
                <ActionsPopOverItem
                  icon={<ExportOutlined />}
                  black
                >
                  Export
                </ActionsPopOverItem>
              </ExportOutboundTransportAction>
              <DeleteOutboundTransportAction
                route={{
                  departure_facility_id: route.departure_id,
                  arrival_facility_id: route.arrival_id,
                  arrival_name: byCategory ? route.transport_category : route.arrival_name,
                  by_transport_category: byCategory,
                }}
                year={year}
              >
                <ActionsPopOverItem
                  icon={<DeleteOutlined />}
                  danger
                >
                  Delete
                </ActionsPopOverItem>
              </DeleteOutboundTransportAction>
            </>
          }
        >
          <MoreOutlined />
        </ActionsPopover>
      ),
      align: "end",
    },
  ];

  const { pagination, filter, search, sort, onSearchFilterSortPaginate, setPage } =
    useTableStateUrl({
      columns,
    });

  const years = useOutboundTransportYearStatus();

  const data = useGetOutboundTransport({
    year,
    pagination,
    filter,
    search,
    sort,
    byTransportCategory: byCategory,
  });

  const totals = useGetOutboundTransportTotals({
    year,
    filter,
    search,
    byTransportCategory: byCategory,
  });

  const tableProps: TableProps<RouteGroup> = {
    columns: columns,
    availableFilters: transportUploadFilters,
    rowKey: "id",
    rowLink: (record) => rowLink(record, byCategory),
    onSearchFilterSortPaginate,
    loading: data.isLoading || data.isFetching,
    data: data.data?.data,
    totalRows: totals.data?.total_count,
    pagination,
    sort,
    filter,
    search,
  };

  const patchCompanySettings = usePatchCompanySettings();

  return (
    <div>
      <PageHeader
        title="Outbound transport"
        description={translation.transport.outboundIntro}
        right={<OutboundTransportButtonGroup />}
      />
      <div>
        <TableHeader
          left={
            <>
              <YearSelector
                optionData={years.data}
                isLoading={years.isLoading}
                onChange={() => setPage(1)}
              />
              <PageButtonGroup>
                <ExportOutboundTransportAction year={year}>
                  <Button icon={<ExportOutlined />}>Export</Button>
                </ExportOutboundTransportAction>
                <MoreButton
                  onOpenChange={setIsMoreOpen}
                  open={isMoreOpen}
                  actions={
                    <>
                      <DeleteOutboundTransportAction
                        year={year}
                        onSuccess={() => setIsMoreOpen(false)}
                      >
                        <ActionsPopOverItem
                          icon={<DeleteOutlined />}
                          danger
                        >
                          Delete
                        </ActionsPopOverItem>
                      </DeleteOutboundTransportAction>
                    </>
                  }
                />
              </PageButtonGroup>
              <SwitchWithLabel
                size="small"
                checked={companySettings?.transport_show_by_category}
                onChange={() =>
                  patchCompanySettings.mutate({
                    transport_show_by_category: !companySettings?.transport_show_by_category,
                  })
                }
              >
                View per transport category
              </SwitchWithLabel>
            </>
          }
          right={
            <>
              <StatusTags
                data={totals.data}
                loading={totals.isLoading || totals.isFetching}
                calculatingTooltip="Root is calculating the impact of some transport legs in the background. All you have to do is wait ⏳"
                errorTooltip="Root has not been able to calculate the impact of some legs. Click a route with errors to see what's going on. 🧐"
              />
            </>
          }
        />
        <Table {...tableProps} />
      </div>
    </div>
  );
}
